import { useLayoutEffect, useRef, useState } from "react"
import { Dayjs } from "dayjs"
import styled from "styled-components"

import { computeDropdownPosition } from "utils/computeDropdownPosition"
import { useOutsideClick } from "utils/useOutsideClick"
import DateWithSelectTimePicker from "components/common/DateWithSelectTimePicker.tsx/DateWithSelectTimePicker"
import { ButtonNew } from "components/form/ButtonNew"

const Container = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  width: 290px;
  overflow-x: hidden;
  background-color: var(--surface-1);
  z-index: 1001;
  box-shadow:
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`

const Description = styled.div`
  color: var(--text-muted);
  padding: 0 18px;
  font-size: 12px;
  margin-bottom: 10px;
`

const BottomContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 10px 18px 0;
  margin-bottom: 10px;
  gap: 8px;
  border-top: 1px solid #eaeaea;

  span {
    flex: 1;
  }
`

interface MessageScheduleDateTimePickerProps {
  onCancel: () => void
  onSubmit: () => void
  selectedDateTime: Dayjs | null
  onDateTimeChange: (dateTime: Dayjs) => void
  align?: "left" | "right"
}

const MessageScheduleDateTimePicker = ({
  onCancel,
  onSubmit,
  selectedDateTime,
  onDateTimeChange,
  align = "left",
}: MessageScheduleDateTimePickerProps) => {
  const container = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ left: number; top: number; maxHeight: number } | null>(null)

  useOutsideClick(container, () => onCancel(), true)

  useLayoutEffect(() => {
    const updatePosition = () => {
      const element = container.current
      const anchor = element?.parentElement
      if (!element || !anchor) return

      const next = computeDropdownPosition(
        anchor.getBoundingClientRect(),
        element.offsetWidth,
        element.scrollHeight,
        align,
        window.innerWidth,
        window.innerHeight,
      )

      setPosition(prev =>
        prev && prev.left === next.left && prev.top === next.top && prev.maxHeight === next.maxHeight ? prev : next,
      )
    }

    updatePosition()
    const resizeObserver = new ResizeObserver(updatePosition)
    if (content.current) resizeObserver.observe(content.current)
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [align])

  return (
    <Container
      ref={container}
      style={
        position
          ? { left: position.left, top: position.top, maxHeight: position.maxHeight, overflowY: "auto" }
          : { visibility: "hidden" }
      }
    >
      <ContentWrapper ref={content}>
        <DateWithSelectTimePicker onDateTimeChange={onDateTimeChange} />
        {selectedDateTime && (
          <Description>
            Message will be sent to the customer on {selectedDateTime.format("MMMM DD, YYYY hh:mm A")}
          </Description>
        )}
        <BottomContainer>
          <ButtonNew color="white" onClick={onCancel}>
            Cancel
          </ButtonNew>
          <ButtonNew color="blue" onClick={onSubmit} disabled={!selectedDateTime}>
            Schedule
          </ButtonNew>
        </BottomContainer>
      </ContentWrapper>
    </Container>
  )
}

export default MessageScheduleDateTimePicker
